import { useState } from "react";
import { Share2, Twitter, Facebook, Linkedin, Link2, Check, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export default function ShareScoreCard({ business, score, scoreZone, placeId, slug }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = slug
    ? `${window.location.origin}/shared/${slug}`
    : `${window.location.origin}/report/${placeId}`;

  const scoreLabel = score >= 80 ? "Optimization Zone" : score >= 60 ? "Growth Zone" : "Danger Zone";
  const scoreColor = score >= 80 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444";
  const zoneName = scoreZone?.label || scoreLabel;

  const shareText = `${business?.name || "My business"} scored ${score}/100 on the RankifyLocal SEO Audit (${zoneName}). Check your score free:`;

  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  const platforms = [
    { name: "Twitter", icon: Twitter, url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, color: "#1DA1F2" },
    { name: "Facebook", icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, color: "#1877F2" },
    { name: "LinkedIn", icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, color: "#0A66C2" },
    { name: "WhatsApp", icon: MessageCircle, url: `https://wa.me/?text=${encodedText}%20${encodedUrl}`, color: "#25D366" },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement("input");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section
      id="share"
      data-testid="share-score-card"
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 16,
        padding: "28px 24px",
        marginTop: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Share2 style={{ width: 20, height: 20, color: "#3B5BDB" }} />
        <h3 style={{ fontSize: 18, fontWeight: 800, color: "#111827", margin: 0 }}>Share Your Score</h3>
      </div>

      {/* Score preview card */}
      <div
        data-testid="share-preview-card"
        style={{
          background: "linear-gradient(135deg, #111827 0%, #1e293b 100%)",
          borderRadius: 12,
          padding: "24px 20px",
          marginBottom: 20,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: scoreColor, opacity: 0.1 }} />
        <p style={{ fontSize: 13, fontWeight: 500, color: "#94a3b8", marginBottom: 8, letterSpacing: "0.03em" }}>RANKIFYLOCAL AUDIT</p>
        <p style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{business?.name || "Your Business"}</p>
        <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>{business?.address || ""}</p>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 48, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{score}</span>
          <span style={{ fontSize: 18, fontWeight: 600, color: "#64748b" }}>/100</span>
        </div>
        <div style={{
          display: "inline-block",
          marginTop: 8,
          padding: "4px 12px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 700,
          background: `${scoreColor}22`,
          color: scoreColor,
        }}>
          {zoneName}
        </div>
      </div>

      {/* Share buttons */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {platforms.map(({ name, icon: Icon, url, color }) => (
          <a
            key={name}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${name.toLowerCase()}-btn`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#374151",
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              textDecoration: "none",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#374151"; }}
          >
            <Icon style={{ width: 15, height: 15 }} />
            {name}
          </a>
        ))}
      </div>

      {/* Copy link */}
      <button
        onClick={copyLink}
        data-testid="share-copy-link-btn"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "10px 14px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          color: copied ? "#22c55e" : "#6B7280",
          background: copied ? "#F0FDF4" : "#F9FAFB",
          border: `1px solid ${copied ? "#86efac" : "#E5E7EB"}`,
          cursor: "pointer",
          transition: "all 0.2s",
        }}
      >
        {copied ? <Check style={{ width: 15, height: 15 }} /> : <Link2 style={{ width: 15, height: 15 }} />}
        {copied ? "Link copied!" : "Copy share link"}
      </button>
    </section>
  );
}
